import { makeAutoObservable, runInAction } from "mobx";
import { IMerchantOnboarding } from "../onboarding-types";
import OnboardingDataStore, {
    IGetMerchantOnboardingModel,
    IOnboardingScreenStore
} from "./onboarding-data-store";
import { ONBOARDING_STEPS } from "../constants";
import { OnboardingNavigationStore } from "./onboarding-navigation-store";
import TermsAcceptanceService from "../../../services/api/terms-acceptance-service";
import { rootContainer, TYPE } from "../../../services/container/root";
import dashboardSettingsStore from "../../../stores/dashboard-settings-store";

class AcceptTermsScreenStore implements IOnboardingScreenStore {
    private _getModel: IGetMerchantOnboardingModel;
    private _onboardingDataStore: OnboardingDataStore;
    private _isLoading = false;

    constructor(onboardingDataStore: OnboardingDataStore, private _navigationStore: OnboardingNavigationStore) {
        this._onboardingDataStore = onboardingDataStore;
        this._getModel = this._onboardingDataStore.getModel;
        makeAutoObservable(this, {}, { autoBind: true });
    }

    private get termsAcceptanceService(): TermsAcceptanceService {
        return rootContainer.get<TermsAcceptanceService>(TYPE.TermsAcceptanceService);
    }

    public get isLoading(): boolean {
        return this._isLoading;
    }

    public get termsContent(): string {
        return this._onboardingDataStore.getSetting()?.terms_content
            ?? dashboardSettingsStore.termsContent
            ?? '';
    }

    public get termsVersion(): string | null {
        return this._onboardingDataStore.getSetting()?.terms_version
            ?? dashboardSettingsStore.termsVersion;
    }

    public isComplete(): boolean {
        return this._onboardingDataStore.getSetting()?.merchant?.are_terms_accepted ?? false;
    }

    public getUpdatedModel(): IMerchantOnboarding {
        return { ...this._getModel() } as IMerchantOnboarding;
    }

    public getMixPanelData(): Record<string, unknown> {
        return {};
    }

    public hasChanges(): boolean {
        return false;
    }

    public discardChanges(): void {}

    public previousStep(): void {
        this._navigationStore.previousStep();
    }

    public async acceptTerms(): Promise<void> {
        if (this.isComplete()) {
            this._navigationStore.nextStep();
            return;
        }

        const termsVersion = this.termsVersion;
        if (!termsVersion) {
            console.error('[onboarding wizard] missing terms version on accept terms screen');
            return;
        }

        this._isLoading = true;
        try {
            const result = await this.termsAcceptanceService.acceptTerms(termsVersion);
            dashboardSettingsStore.applyTermsAcceptanceSettings(result);
            this._onboardingDataStore.markTermsAccepted();
            runInAction(() => {
                this._navigationStore.nextStep();
            });
        } finally {
            runInAction(() => {
                this._isLoading = false;
            });
        }
    }
}

export default AcceptTermsScreenStore;
